import React from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Stack,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useSubmitEvaluationMutation } from 'redux/reducers/assignmentEvalutaion';

interface Question {
  question_id: number;
  question_title: string;
  option_a?: string;
  option_b?: string;
  option_c?: string;
  option_d?: string;
  marks: number;
}

interface PreTestDetails {
  evaluation_id: number;
  title: string;
  total_marks: number;
  questions: Question[];
}

interface PreTestProps {
  pretestDetails: PreTestDetails;
  handleRoutineTabChange: VoidFunction;
}

const PreTest: React.FC<PreTestProps> = ({ pretestDetails, handleRoutineTabChange }) => {
  const [submitEvaluation, { isLoading, isSuccess, isError, error }] =
    useSubmitEvaluationMutation();

  const validationSchema = Yup.object().shape(
    pretestDetails?.questions?.reduce((schema, question) => {
      schema[`question_${question.question_id}`] = Yup.string().required('Please select an answer');
      return schema;
    }, {} as any)
  );

  const formik = useFormik({
    initialValues: pretestDetails?.questions.reduce((values, question) => {
      values[`question_${question.question_id}`] = '';
      return values;
    }, {} as any),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const answers = pretestDetails?.questions.map((q) => ({
        question_id: q.question_id,
        selected_option: values[`question_${q.question_id}`]
      }));

      const payload = {
        evaluation_id: pretestDetails.evaluation_id,
        answers
      };

      try {
        await submitEvaluation(payload).unwrap();
        handleRoutineTabChange();
        toast.success('Pre-test submitted successfully!');
        formik.resetForm();
      } catch (err: any) {
        toast.error(err?.data?.message || 'Failed to submit pre-test');
      }
    }
  });

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom align="center" color="primary">
          {pretestDetails?.title} (Pre-Test)
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Total Marks: {pretestDetails?.total_marks}
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={5} mt={4}>
            {pretestDetails?.questions &&
              pretestDetails?.questions?.length > 0 &&
              pretestDetails?.questions.map((question, index) => {
                const fieldName = `question_${question.question_id}`;
                const selectedValue = formik.values[fieldName];

                return (
                  <Box key={question.question_id}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      {index + 1}. {question.question_title}
                      <span style={{ color: '#01579b', marginLeft: 8 }}>
                        ({question.marks} marks)
                      </span>
                    </Typography>

                    <FormControl
                      component="fieldset"
                      error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}>
                      <RadioGroup
                        name={fieldName}
                        value={selectedValue}
                        onChange={(e) => formik.setFieldValue(fieldName, e.target.value)}>
                        <Stack direction="row" spacing={1} ml={2}>
                          {question.option_a && (
                            <FormControlLabel
                              value="A"
                              control={<Radio />}
                              label={
                                <Typography variant="body1">A) {question.option_a}</Typography>
                              }
                            />
                          )}
                          {question.option_b && (
                            <FormControlLabel
                              value="B"
                              control={<Radio />}
                              label={
                                <Typography variant="body1">B) {question.option_b}</Typography>
                              }
                            />
                          )}
                          {question.option_c && (
                            <FormControlLabel
                              value="C"
                              control={<Radio />}
                              label={
                                <Typography variant="body1">C) {question.option_c}</Typography>
                              }
                            />
                          )}
                          {question.option_d && (
                            <FormControlLabel
                              value="D"
                              control={<Radio />}
                              label={
                                <Typography variant="body1">D) {question.option_d}</Typography>
                              }
                            />
                          )}
                        </Stack>
                      </RadioGroup>

                      {/* Validation Error */}
                      {formik.touched[fieldName] && formik.errors[fieldName] && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ ml: 2, mt: 1, display: 'block' }}>
                          {formik.errors[fieldName] as string}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                );
              })}

            {/* Submit Button */}
            <Box textAlign="center" mt={4}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading || !formik.isValid || !formik.dirty}>
                {isLoading ? <CircularProgress size={24} /> : 'Submit Pre-Test'}
              </Button>
            </Box>

            {/* Success/Error Feedback */}
            {isSuccess && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Pre-test submitted successfully!
              </Alert>
            )}
            {isError && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {(error as any)?.data?.message || 'Submission failed. Please try again.'}
              </Alert>
            )}
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default PreTest;
